// Custom hook bundling the mutation operations the blog editor performs
// on its page/element data.
//
// Extracted from BlogsPage.js. These functions have side effects (10+
// distinct setters, toast notifications). Per the refactor plan, this first
// pass relocates them as-is — function bodies are unchanged — and exposes
// them via a hook so the closure over component state and setters is
// re-formed inside this module rather than being implicit in BlogsPage.
//
// Call sites in BlogsPage JSX do NOT need to change: the destructured
// names returned by this hook are the same as the original function names.
//
// DEAD CODE FLAG: `updateElementStyle` and `updateOfferElementHtml` are
// defined but never called anywhere in the codebase as of extraction.
// Preserved verbatim (Rule 3, surgical changes); flag for future cleanup.

import { generateElementHTML } from './blogHtmlGenerators';

/**
 * @param {object} deps - All the state, setters, and helpers the mutations need.
 * @param {Array} deps.pages
 * @param {number} deps.currentPageIndex
 * @param {number} deps.nextElementId
 * @param {number} deps.nextPageId
 * @param {object|null} deps.selectedElement
 * @param {object|null} deps.selectedOffer
 * @param {string} deps.selectedElementHtml
 * @param {Function} deps.setPages
 * @param {Function} deps.setNextElementId
 * @param {Function} deps.setNextPageId
 * @param {Function} deps.setCurrentPageIndex
 * @param {Function} deps.setSelectedElementIndex
 * @param {Function} deps.getCurrentPage
 * @param {Function} deps.getCurrentPageElements
 * @param {Function} deps.getDefaultStyles
 * @param {Function} deps.generateAffiliateLink
 * @param {Function} deps.getPreset
 * @param {Function} deps.getButtonPreset
 * @param {object} deps.toast - react-toastify toast object
 */
export function useBlogElementMutations({
  pages,
  currentPageIndex,
  nextElementId,
  nextPageId,
  selectedElement,
  selectedOffer,
  selectedElementHtml,
  setPages,
  setNextElementId,
  setNextPageId,
  setCurrentPageIndex,
  setSelectedElementIndex,
  getCurrentPage,
  getCurrentPageElements,
  getDefaultStyles,
  generateAffiliateLink,
  getPreset,
  getButtonPreset,
  toast,
}) {
  const addElement = (type) => {
    // Special handling for offer elements with pagination
    if (type === 'offer') {
      if (!selectedElement) {
        toast.error('Please select an offer element first');
        return;
      }

      console.log('🎯 Inserting offer element:', selectedElement);

      // Parse the element content to check for pagination
      let parsedContent = null;
      try {
        if (typeof selectedElement.content === 'string') {
          parsedContent = JSON.parse(selectedElement.content);
        } else {
          parsedContent = selectedElement.content;
        }
      } catch (error) {
        console.error('Error parsing element content:', error);
      }

      // Check if this is a paginated offer element
      if (parsedContent && parsedContent.pagination && parsedContent.pagination.enabled) {
        console.log('📄 Paginated offer element detected!', parsedContent.pagination);

        const { pagination } = parsedContent;
        const allElements = parsedContent.elements || [];

        // Reconstruct pages from saved pagination data
        const newPages = [];
        let elementIndex = 0;
        let currentElementId = nextElementId;

        for (let i = 0; i < pagination.pages.length; i++) {
          const pageInfo = pagination.pages[i];
          const pageElements = allElements.slice(elementIndex, elementIndex + pageInfo.elementCount);

          // Generate HTML content for the entire page
          let pageHtmlContent = '';

          pageElements.forEach(element => {
            console.log('🔄 Processing element for HTML:', element.type, element);

            // Generate HTML for each element and add affiliate links
            let elementHtml = '';

            // Use centralized HTML generation for all element types
            elementHtml = generateElementHTML(element);

            // Replace any href with affiliate link
            if (selectedOffer && elementHtml) {
              const affiliateUrl = generateAffiliateLink(selectedOffer);
              elementHtml = elementHtml.replace(
                /href=["']([^"']*)["']/g,
                `href="${affiliateUrl}"`
              );
            }

            pageHtmlContent += elementHtml;
          });

          // Create a single offer element for each page
          // Aggressively compress HTML content to reduce document size
          // This is critical for merged blogs with many paginated pages
          const compressedHtmlContent = pageHtmlContent
            .replace(/\s+/g, ' ') // Replace multiple whitespace with single space
            .replace(/>\s+</g, '><') // Remove spaces between tags
            .replace(/\s*{\s*/g, '{') // Remove spaces around braces in styles
            .replace(/\s*}\s*/g, '}') // Remove spaces around braces in styles
            .replace(/\s*:\s*/g, ':') // Remove spaces around colons in styles
            .replace(/\s*;\s*/g, ';') // Remove spaces around semicolons
            .replace(/<!--[\s\S]*?-->/g, '') // Remove HTML comments
            .trim();

          const offerElement = {
            id: currentElementId++,
            type: 'offer',
            content: 'OFFER_ELEMENT',
            styles: getDefaultStyles('offer'),
            htmlContent: compressedHtmlContent, // Store compressed HTML
            offerName: selectedOffer?.title || 'Unknown Offer',
            elementName: `${selectedElement?.name || 'Unnamed Element'} - Page ${i + 1}`,
            offerId: selectedOffer?._id
          };

          newPages.push({
            id: nextPageId + i,
            elements: [offerElement] // Single offer element per page
          });

          console.log(`📄 Created page ${i + 1} with single offer element`);
          elementIndex += pageInfo.elementCount;
        }

        // Check if current page is empty
        const currentPage = getCurrentPage();
        const isCurrentPageEmpty = !currentPage || currentPage.elements.length === 0;

        if (isCurrentPageEmpty && newPages.length > 0) {
          console.log('📄 Current page is empty - replacing with first paginated page');

          // Replace current empty page with first paginated page, add rest as new pages
          const [firstPage, ...restPages] = newPages;
          setPages(prev => {
            const updatedPages = [...prev];
            updatedPages[currentPageIndex] = firstPage;
            return [...updatedPages, ...restPages];
          });
          setNextPageId(prev => prev + restPages.length);

          // Stay on current page (now filled with first paginated page)
          toast.success(`Inserted paginated offer element with ${newPages.length} page(s)`);
        } else {
          console.log('📄 Current page has content - adding paginated pages after existing pages');

          // Add the new pages to the blog after existing pages
          setPages(prev => [...prev, ...newPages]);
          setNextPageId(prev => prev + newPages.length);

          // Switch to the first new page
          setCurrentPageIndex(pages.length);

          toast.success(`Inserted paginated offer element with ${newPages.length} page(s)`);
        }

        setNextElementId(currentElementId);
        return;
      } else {
        // Non-paginated offer element - create single element with HTML
        console.log('🎯 Non-paginated offer element - inserting as single element');

        const newElement = {
          id: nextElementId,
          type: 'offer',
          content: 'OFFER_ELEMENT',
          styles: getDefaultStyles(type),
          htmlContent: selectedElementHtml,
          offerName: selectedOffer?.title || 'Unknown Offer',
          elementName: selectedElement?.name || 'Unnamed Element',
          offerId: selectedOffer?._id
        };

        const currentPage = getCurrentPage();
        const currentPageElements = currentPage.elements;

        console.log('➕ Adding non-paginated offer element:', newElement);

        // Add element to current page first
        setPages(prev => prev.map((page, pageIndex) =>
          pageIndex === currentPageIndex
            ? { ...page, elements: [...page.elements, newElement] }
            : page
        ));

        setNextElementId(prev => prev + 1);

        // OVERFLOW DETECTION DISABLED - Users control pagination visually through live preview
        console.log('✅ Offer element added to current page (overflow detection disabled)');

        return; // Exit early for offer elements
      }
    }

    // For non-offer elements
    // Set default presets for title and text elements
    let defaultPresetName;
    let defaultPresetStyles = {};

    if (type === 'title') {
      defaultPresetName = 'heading1';
      defaultPresetStyles = getPreset('heading1');
    } else if (type === 'text') {
      defaultPresetName = 'paragraph1';
      defaultPresetStyles = getPreset('paragraph1');
    }

    const newElement = {
      id: nextElementId,
      type,
      content: type === 'media' ? 'Click Here' : '',
      url: type === 'media' ? '#' : undefined,
      showOverlayButton: false, // Default to false so user can "Insert" it manually
      videoFile: '',
      mediaUrl: '',
      styles: type === 'title' || type === 'text'
        ? { ...getDefaultStyles(type), ...defaultPresetStyles, textAlign: 'left' }
        : { ...getDefaultStyles(type), ...(type === 'media' ? getButtonPreset('buttonGradient') : {}), ...(type === 'media' ? { presetStyle: 'buttonGradient' } : {}) },
      framePresetName: type === 'media' ? 'landscape' : undefined,
      presetName: defaultPresetName,
      // Add default structure for bullets
      ...(type === 'bullets' && {
        rows: [
          { icon: 'check', text: 'List item 1' },
          { icon: 'check', text: 'List item 2' },
          { icon: 'check', text: 'List item 3' },
        ],
        textStylePreset: 'paragraph1'
      })
    };



    // OVERFLOW DETECTION DISABLED - Users control pagination visually through live preview
    // All elements are now added to the current page regardless of calculated height
    const currentPageElements = getCurrentPage().elements;
    console.log('✅ Adding element to current page (overflow detection disabled)');

    // Add element to current page
    setPages(prev => prev.map((page, index) =>
      index === currentPageIndex
        ? { ...page, elements: [...page.elements, newElement] }
        : page
    ));

    // Automatically expand the newly added element for editing
    setSelectedElementIndex(currentPageElements.length); // New element will be at the end

    setNextElementId(prev => prev + 1);
  };

  const removeElement = (index) => {
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? { ...page, elements: page.elements.filter((_, i) => i !== index) }
        : page
    ));
  };

  const duplicateElement = (index) => {
    const currentElements = getCurrentPageElements();
    const elementToDuplicate = currentElements[index];
    const duplicatedElement = {
      ...elementToDuplicate,
      id: nextElementId,
      content: elementToDuplicate.content + ' (Copy)'
    };

    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? { ...page, elements: [...page.elements, duplicatedElement] }
        : page
    ));
    setNextElementId(prev => prev + 1);
  };

  const moveElement = (index, direction) => {
    const currentElements = getCurrentPageElements();
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    // Check if moving within the same page
    if (newIndex >= 0 && newIndex < currentElements.length) {
      // Move within current page
      const newElements = [...currentElements];
      [newElements[index], newElements[newIndex]] = [newElements[newIndex], newElements[index]];

      setPages(prev => prev.map((page, pageIndex) =>
        pageIndex === currentPageIndex
          ? { ...page, elements: newElements }
          : page
      ));
    } else if (direction === 'up' && index === 0 && currentPageIndex > 0) {
      // Move to previous page (to the end)
      const element = currentElements[index];
      const previousPageIndex = currentPageIndex - 1;

      setPages(prev => prev.map((page, pageIndex) => {
        if (pageIndex === currentPageIndex) {
          // Remove element from current page
          return { ...page, elements: currentElements.slice(1) };
        } else if (pageIndex === previousPageIndex) {
          // Add element to end of previous page
          return { ...page, elements: [...page.elements, element] };
        }
        return page;
      }));

      // Switch to previous page
      setCurrentPageIndex(previousPageIndex);
    } else if (direction === 'down' && index === currentElements.length - 1 && currentPageIndex < pages.length - 1) {
      // Move to next page (to the beginning)
      const element = currentElements[index];
      const nextPageIndex = currentPageIndex + 1;

      setPages(prev => prev.map((page, pageIndex) => {
        if (pageIndex === currentPageIndex) {
          // Remove element from current page
          return { ...page, elements: currentElements.slice(0, -1) };
        } else if (pageIndex === nextPageIndex) {
          // Add element to beginning of next page
          return { ...page, elements: [element, ...page.elements] };
        }
        return page;
      }));

      // Switch to next page
      setCurrentPageIndex(nextPageIndex);
    }
  };

  const updateElementContent = (index, content) => {
    const currentPage = getCurrentPage();
    const currentPageElements = currentPage.elements;

    // OVERFLOW DETECTION DISABLED - Users control pagination visually through live preview
    // Update element content directly without overflow checks
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) =>
            i === index ? { ...element, content } : element
          )
        }
        : page
    ));
  };

  // DEAD CODE at extraction time — no call sites in the codebase. Preserved
  // verbatim for behavioral fidelity. Safe to delete after independent review.
  const updateElementStyle = (index, property, value) => {
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) =>
            i === index
              ? { ...element, styles: { ...element.styles, [property]: value } }
              : element
          )
        }
        : page
    ));
  };

  // DEAD CODE at extraction time — no call sites in the codebase. Preserved
  // verbatim for behavioral fidelity. Safe to delete after independent review.
  const updateOfferElementHtml = (index, htmlContent) => {
    // OVERFLOW DETECTION DISABLED - Users control pagination visually through live preview
    // Update offer element HTML directly without overflow checks
    setPages(prev => prev.map((page, pageIndex) =>
      pageIndex === currentPageIndex
        ? {
          ...page,
          elements: page.elements.map((element, i) =>
            i === index ? { ...element, htmlContent } : element
          )
        }
        : page
    ));
  };

  return {
    addElement,
    removeElement,
    duplicateElement,
    moveElement,
    updateElementContent,
    updateElementStyle,
    updateOfferElementHtml,
  };
}
